import React, { useState } from "react";
import { useProjects } from "../resume/useResumeData";
import DashboardLoader from "./DashboardLoader";
import Error from "../../ui/Error";
import EditSvg from "../../ui/EditSvg";
import DeleteSvg from "../../ui/DeleteSvg";
import ProjectsModal from "./ProjectsModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";



import { getImageUrl } from "../../helper/imageHelper";

const ProjectsManager = () => {
    const { projects, isLoading, deleteProject, isDeleting } = useProjects();
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const handleDelete = (id) => {
        setDeleteId(id);
    };

    const handleConfirmDelete = () => {
        if (deleteId) {
            deleteProject(deleteId, {
                onSuccess: () => setDeleteId(null)
            });
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    if (isLoading) return <DashboardLoader />;
    if (!projects) return <Error />;

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Projects</h1>
                    <p className="text-sm text-gray-500">Manage your portfolio projects</p>
                </div>

                <button
                    onClick={handleAddNew}
                    className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        <svg className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Project
                    </span>
                    <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </button>
            </div>

            {projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <p>No projects yet — add your first one above.</p>
                </div>
            ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {projects.map((item) => (
                    <div
                        key={item.id}
                        className="group relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-[#1a1f2e] dark:text-white"
                    >
                        {item.image && (
                            <div className="h-40 w-full overflow-hidden">
                                <img loading="lazy" src={getImageUrl(item.image)} alt={item.title} className="h-full w-full object-cover object-top transition-all duration-[5000ms] group-hover:object-bottom" />
                            </div>
                        )}

                        <div className="flex flex-1 flex-col p-4">
                            <div className="mb-2 flex items-start justify-between">
                                <h4 className="font-bold text-lg leading-tight">{item.title}</h4>
                                <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    {item.category}
                                </span>
                            </div>

                            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                                {item.description}
                            </p>

                            {/* Tags/Tech */}
                            <div className="mb-4 flex flex-wrap gap-1">
                                {item.technologies?.slice(0, 4).map((tech, idx) => (
                                    <span key={idx} className="rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                        {tech}
                                    </span>
                                ))}
                                {item.technologies?.length > 4 && (
                                    <span className="text-xs text-gray-400">+{item.technologies.length - 4}</span>
                                )}
                            </div>

                            <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-4 dark:border-gray-700">
                                <div className="flex gap-2">
                                    {item.link && (
                                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary">
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                        </a>
                                    )}
                                    {item.github_link && (
                                        <a href={item.github_link} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary">
                                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" /></svg>
                                        </a>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEdit(item)}
                                        className="transform rounded-full p-2 text-blue-500 transition-all duration-300 hover:bg-blue-50 hover:scale-110 hover:shadow-md dark:hover:bg-blue-900/20"
                                        title="Edit Project"
                                    >
                                        <EditSvg className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(item.id)}
                                        disabled={isDeleting}
                                        className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                                        title="Delete Project"
                                    >
                                        <DeleteSvg className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            )}

            {isModalOpen && (
                <ProjectsModal
                    itemToEdit={editingItem}
                    onClose={() => setIsModalOpen(false)}
                />
            )}

            <DeleteConfirmationModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleConfirmDelete}
                title="this project"
                entityLabel="Project"
            />
        </div>
    );
};

export default ProjectsManager;
